import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { Menu, Transition } from '@headlessui/react';
import {
  SunIcon,
  MoonIcon,
  UserIcon,
  ChevronRightIcon,
  MagnifyingGlassIcon,
  BellIcon,
} from '@heroicons/react/24/outline';
import { Fragment } from 'react';
import RegionSelector from '../Region/RegionSelector';
import LanguageSwitcher from '../LanguageSwitcher';

const TopBar: React.FC = () => {
  const { t } = (useTranslation as any)('common');
  const { user, logout } = useAuth();
  const { isDarkMode, toggleDarkMode } = useTheme();
  const location = useLocation();

  const getRouteLabel = (segment: string): string => {
    const key = `nav.${segment}`;
    const translated = t(key);
    return translated !== key ? translated : segment;
  };

  // Generate breadcrumbs from location
  const pathSegments = location.pathname.split('/').filter(Boolean);
  const breadcrumbs = pathSegments.map((segment, index) => {
    const path = '/' + pathSegments.slice(0, index + 1).join('/');
    const label = getRouteLabel(segment);
    return { label, path };
  });

  return (
    <header className="h-[52px] bg-surface-1 border-b border-border-0 flex items-center justify-between px-4 md:px-6 flex-shrink-0">
      {/* Left: Breadcrumbs */}
      <nav className="flex items-center text-[12px] min-w-0 ml-8 md:ml-0" aria-label="Breadcrumb">
        {breadcrumbs.map((crumb, index) => (
          <Fragment key={crumb.path}>
            {index > 0 && (
              <ChevronRightIcon className="w-3 h-3 text-text-quaternary mx-1.5 flex-shrink-0" />
            )}
            {index === breadcrumbs.length - 1 ? (
              <span className="text-text-primary font-medium truncate">
                {crumb.label}
              </span>
            ) : (
              <Link
                to={crumb.path}
                className="text-text-tertiary hover:text-text-primary transition-colors truncate"
              >
                {crumb.label}
              </Link>
            )}
          </Fragment>
        ))}
      </nav>

      {/* Right: Region selector, Search, notifications, theme toggle, user avatar */}
      <div className="flex items-center gap-1.5">
        {/* Region Selector */}
        <RegionSelector />

        {/* Language Switcher */}
        <LanguageSwitcher />

        {/* Separator */}
        <div className="hidden sm:block w-px h-5 bg-border-0 mx-0.5" />

        {/* Search (visual only) */}
        <button className="p-2 rounded-lg text-text-tertiary hover:text-text-primary hover:bg-surface-3 transition-colors hidden sm:flex">
          <MagnifyingGlassIcon className="w-[18px] h-[18px]" strokeWidth={1.6} />
        </button>

        {/* Notifications (visual only) */}
        <button className="p-2 rounded-lg text-text-tertiary hover:text-text-primary hover:bg-surface-3 transition-colors hidden sm:flex">
          <BellIcon className="w-[18px] h-[18px]" strokeWidth={1.6} />
        </button>

        {/* Theme Toggle */}
        <button
          onClick={toggleDarkMode}
          className="p-2 rounded-lg text-text-tertiary hover:text-text-primary hover:bg-surface-3 transition-colors"
          aria-label={isDarkMode ? String(t('topbar.lightMode')) : String(t('topbar.darkMode'))}
        >
          {isDarkMode ? (
            <SunIcon className="w-[18px] h-[18px]" strokeWidth={1.6} />
          ) : (
            <MoonIcon className="w-[18px] h-[18px]" strokeWidth={1.6} />
          )}
        </button>

        {/* User Avatar / Profile Dropdown */}
        <Menu as="div" className="relative">
          <Menu.Button className="flex items-center gap-2 p-1.5 rounded-lg hover:bg-surface-3 transition-colors focus:outline-none focus:ring-2 focus:ring-accent-500/40 focus:ring-offset-0">
            <div className="h-7 w-7 rounded-full bg-gradient-to-br from-accent-500 to-accent-700 flex items-center justify-center">
              <span className="text-[11px] font-semibold text-white">
                {user?.prenom?.[0]?.toUpperCase() || ''}{user?.nom?.[0]?.toUpperCase() || ''}
              </span>
            </div>
          </Menu.Button>

          <Transition
            as={Fragment}
            enter="transition ease-out duration-100"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
          >
            <Menu.Items className="absolute right-0 z-50 mt-1.5 w-56 origin-top-right rounded-card bg-surface-2 border border-border-1 shadow-card-hover focus:outline-none overflow-hidden">
              <div className="px-4 py-3 border-b border-border-0">
                <p className="text-[12.5px] font-semibold text-text-primary">{user?.prenom} {user?.nom}</p>
                <p className="text-[11px] text-text-tertiary truncate">{user?.email}</p>
              </div>
              <div className="py-1">
                <Menu.Item>
                  {({ active }) => (
                    <Link
                      to="/profile"
                      className={`block px-4 py-2 text-[12.5px] text-text-secondary ${active ? 'bg-surface-3' : ''}`}
                    >
                      {t('topbar.profile')}
                    </Link>
                  )}
                </Menu.Item>
                <Menu.Item>
                  {({ active }) => (
                    <button
                      onClick={logout}
                      className={`block w-full text-left px-4 py-2 text-[12.5px] text-red-400 ${active ? 'bg-surface-3' : ''}`}
                    >
                      {t('topbar.logout')}
                    </button>
                  )}
                </Menu.Item>
              </div>
            </Menu.Items>
          </Transition>
        </Menu>
      </div>
    </header>
  );
};

export default TopBar;
